// src/components/admin/UserLimitRequestsTable.js
import React, { useState } from 'react';
import { userService } from '../../services/apiServices';
import { showSuccessToast, showErrorToast, showLoadingToast, updateToast } from '../../utils/toastUtils';

const UserLimitRequestsTable = ({ requests, onRequestUpdate }) => {
  const [processingRequest, setProcessingRequest] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [approvalData, setApprovalData] = useState({
    newLimit: '',
    adminResponse: ''
  });

  const handleApprove = (request) => {
    setSelectedRequest(request);
    setApprovalData({
      newLimit: request.currentLimit ? (request.currentLimit + request.requestedUsers).toString() : request.requestedUsers.toString(),
      adminResponse: ''
    });
    setShowApprovalModal(true);
  };

  const handleReject = async (request) => {
    if (!window.confirm('Êtes-vous sûr de vouloir rejeter cette demande ?')) {
      return;
    }

    setProcessingRequest(request._id);
    const loadingToastId = showLoadingToast('Rejet de la demande en cours...');

    try {
      // Here you would call the API to reject the request
      // For now, we'll simulate it
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      updateToast(loadingToastId, 'Demande rejetée avec succès', 'success');
      if (onRequestUpdate) {
        onRequestUpdate();
      }
    } catch (error) {
      console.error('Reject request error:', error);
      updateToast(loadingToastId, 'Erreur lors du rejet de la demande', 'error');
    } finally {
      setProcessingRequest(null);
    }
  };

  const handleApprovalSubmit = async (e) => {
    e.preventDefault();
    
    if (!approvalData.newLimit || !approvalData.adminResponse.trim()) {
      showErrorToast('Veuillez remplir tous les champs requis');
      return;
    }

    const newLimit = parseInt(approvalData.newLimit);
    if (isNaN(newLimit) || newLimit < 0) {
      showErrorToast('La nouvelle limite doit être un nombre positif');
      return;
    }

    setProcessingRequest(selectedRequest._id);
    const loadingToastId = showLoadingToast('Approbation de la demande en cours...');

    try {
      // Update the admin's user creation limit
      await userService.updateUserCreationLimits(selectedRequest.requestedBy, {
        maxSimpleUsers: newLimit
      });

      // Here you would also update the request status
      // For now, we'll simulate it
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      updateToast(loadingToastId, 'Demande approuvée et limite mise à jour avec succès', 'success');
      setShowApprovalModal(false);
      setSelectedRequest(null);
      setApprovalData({ newLimit: '', adminResponse: '' });
      
      if (onRequestUpdate) {
        onRequestUpdate();
      }
    } catch (error) {
      console.error('Approve request error:', error);
      updateToast(loadingToastId, 'Erreur lors de l\'approbation de la demande', 'error');
    } finally {
      setProcessingRequest(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'yellow', text: 'En attente' },
      approved: { color: 'green', text: 'Approuvée' },
      rejected: { color: 'red', text: 'Rejetée' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full bg-${config.color}-100 text-${config.color}-800`}>
        {config.text}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      low: { color: 'gray', text: 'Faible' },
      medium: { color: 'blue', text: 'Moyenne' },
      high: { color: 'orange', text: 'Élevée' },
      urgent: { color: 'red', text: 'Urgente' }
    };

    const config = priorityConfig[priority] || priorityConfig.medium;
    
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full bg-${config.color}-100 text-${config.color}-800`}>
        {config.text}
      </span>
    );
  };

  if (!requests || requests.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
        <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <p className="text-gray-500">Aucune demande d'augmentation de limite d'utilisateurs</p>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Demandes d'Augmentation de Limite d'Utilisateurs
          </h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Entreprise / Demandeur
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Limite Actuelle
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Utilisateurs Demandés
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Priorité
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {requests.map((request) => (
                <tr key={request._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {request.companyName}
                      </div>
                      <div className="text-sm text-gray-500">
                        {request.requestedByName} ({request.requestedByEmail})
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {request.currentLimit === null ? 'Illimitée' : request.currentLimit}
                    </div>
                    <div className="text-sm text-gray-500">
                      {request.currentUsage || 0} utilisé(s)
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      +{request.requestedUsers}
                    </div>
                    <div className="text-sm text-gray-500">
                      Nouvelle limite: {request.currentLimit === null ? request.requestedUsers : (request.currentLimit + request.requestedUsers)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getPriorityBadge(request.priority)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(request.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(request.createdAt).toLocaleDateString('fr-FR')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    {request.status === 'pending' && (
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleApprove(request)}
                          className="text-green-600 hover:text-green-900 disabled:opacity-50"
                          disabled={processingRequest === request._id}
                        >
                          Approuver
                        </button>
                        <button
                          onClick={() => handleReject(request)}
                          className="text-red-600 hover:text-red-900 disabled:opacity-50"
                          disabled={processingRequest === request._id}
                        >
                          Rejeter
                        </button>
                      </div>
                    )}
                    <button
                      onClick={() => {
                        // Show justification in a modal or expand row
                        alert(`Justification: ${request.justification}`);
                      }}
                      className="text-blue-600 hover:text-blue-900 ml-2"
                    >
                      Voir détails
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Approval Modal */}
      {showApprovalModal && selectedRequest && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Approuver la Demande
                </h3>
                <button
                  onClick={() => setShowApprovalModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={processingRequest === selectedRequest._id}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <form onSubmit={handleApprovalSubmit} className="space-y-4">
                {/* Request Details */}
                <div className="bg-gray-50 p-3 rounded-md text-sm">
                  <div className="font-medium mb-2">{selectedRequest.companyName}</div>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Demandeur:</span>
                      <span>{selectedRequest.requestedByName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Limite actuelle:</span>
                      <span>{selectedRequest.currentLimit === null ? 'Illimitée' : selectedRequest.currentLimit}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Utilisateurs demandés:</span>
                      <span>+{selectedRequest.requestedUsers}</span>
                    </div>
                  </div>
                </div>

                {/* Justification */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Justification de la demande
                  </label>
                  <div className="bg-gray-50 p-3 rounded-md text-sm text-gray-700 max-h-32 overflow-y-auto">
                    {selectedRequest.justification}
                  </div>
                </div>

                {/* New Limit */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nouvelle limite d'utilisateurs *
                  </label>
                  <input
                    type="number"
                    value={approvalData.newLimit}
                    onChange={(e) => setApprovalData(prev => ({ ...prev, newLimit: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                    required
                    disabled={processingRequest === selectedRequest._id}
                  />
                </div>

                {/* Admin Response */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Réponse de l'administrateur *
                  </label>
                  <textarea
                    value={approvalData.adminResponse}
                    onChange={(e) => setApprovalData(prev => ({ ...prev, adminResponse: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="3"
                    placeholder="Commentaire sur l'approbation..."
                    required
                    disabled={processingRequest === selectedRequest._id}
                  />
                </div>

                {/* Buttons */}
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowApprovalModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
                    disabled={processingRequest === selectedRequest._id}
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors disabled:opacity-50"
                    disabled={processingRequest === selectedRequest._id}
                  >
                    {processingRequest === selectedRequest._id ? 'Traitement...' : 'Approuver'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UserLimitRequestsTable;